import React from 'react';
import { logger } from '@fiverr-private/obs';
import { AfterSigningSuccess } from '../../shared/constants/afterSigningSuccess';
import { globalGrafanaReporter } from '../../events/grafana/grafana.reporter';
import { globalBigQueryReporter } from '../../events/bigQuery/bigQuery.reporter';
import { useGoogleOneTapHandlers } from './hooks/useGoogleOneTapHandlers';
import { useIsFedCMAvailable } from './hooks/useIsFedCMAvailable';
import { GoogleOneTapCore, PromptMomentNotification } from './core';

interface GoogleOneTapProps {
    googleClientId: string;
    afterSigningSuccess?: AfterSigningSuccess;
    inLynxesGoogleOneTapFedCmRollout: boolean;
}

export const GoogleOneTap = ({
    googleClientId,
    afterSigningSuccess,
    inLynxesGoogleOneTapFedCmRollout,
}: GoogleOneTapProps) => {
    const isFedCMAvailable = useIsFedCMAvailable({ inLynxesGoogleOneTapFedCmRollout });
    const { onOneTapSuccess, onOneTapError } = useGoogleOneTapHandlers({
        afterSigningSuccess,
    });

    const handleNotificationReceived = (notification: PromptMomentNotification) => {
        const handleNotDisplayedReason = () => {
            if (!notification.isNotDisplayed()) return;

            const notDisplayedReason = notification.getNotDisplayedReason();
            globalGrafanaReporter.count(
                `social_signing.%{platform}.google_one_tap.not_displayed.${notDisplayedReason}`
            );
        };

        const handleSkippedReason = () => {
            if (!notification.isSkippedMoment()) return;

            const skippedReason = notification.getSkippedReason();
            globalGrafanaReporter.count(`social_signing.%{platform}.google_one_tap.skipped.${skippedReason}`);

            if (skippedReason === 'user_cancel') {
                globalGrafanaReporter.count(`social_signing.%{platform}.google_one_tap.close.${skippedReason}`);
            }
        };

        const handleDismissedReason = () => {
            if (!notification.isDismissedMoment()) return;

            const dismissedReason = notification.getDismissedReason();
            globalGrafanaReporter.count(`social_signing.%{platform}.google_one_tap.dismissed.${dismissedReason}`);
        };

        const handleDisplayed = () => {
            if (!notification.isDisplayed()) return;

            globalGrafanaReporter.count('social_signing.%{platform}.google_one_tap.show');
            globalBigQueryReporter.send('google_one_tap.show', {
                group: 'social flow impression',
                reason: 'system_trigger',
            });
        };

        try {
            if (!isFedCMAvailable) {
                handleNotDisplayedReason();
                handleSkippedReason();
                handleDismissedReason();
                handleDisplayed();
            } else {
                handleDismissedReason();
            }
        } catch (error) {
            logger.error('Error while handling google one tap notification reason', { error });
        }
    };

    const onOneTapCallback = () => {
        globalBigQueryReporter.send('google_one_tap.submit');
        globalGrafanaReporter.count('social_signing.%{platform}.google_one_tap.submit');
    };

    return (
        <GoogleOneTapCore
            googleClientId={googleClientId}
            onOneTapSuccess={onOneTapSuccess}
            onOneTapError={onOneTapError}
            onOneTapCallback={onOneTapCallback}
            onNotificationReceived={handleNotificationReceived}
            isFedCMAvailable={isFedCMAvailable}
        />
    );
};
